import { useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";

import { KEY_STEPS, NOT_YET_TAG } from "./work.constants.ts";
import type { WorkTabId } from "./work.constants.ts";
import type { WorkTabView } from "./work.model.ts";

/**
 * The five things a record can be read as, and the one action beside them.
 *
 * EVERY TAB CAN BE ANSWERED, SINCE OND-38. The strip used to carry one that
 * could not — Catalogue Raisonné — drawn quieter than its peers but selectable
 * rather than disabled, for the reason the sidebar gives: a greyed-out tab says
 * only that something is missing, while opening it says what. The citation is
 * the Significant publications tab now, so there is nothing left to draw
 * quietly and no `ready` flag to draw it by.
 *
 * A tablist by role, which is a contract and not a decoration: the announcement
 * comes free but the arrow keys do not, so they are implemented below along with
 * the roving tabindex that makes the strip one stop rather than one per tab. The panel
 * each tab controls is rendered by the caller, hence `aria-controls` naming one
 * stable id rather than one per tab.
 */
export function WorkTabs({
  tabs,
  panelId,
  action,
  onSelect,
}: {
  tabs: WorkTabView[];
  panelId: string;
  /** The primary action for the open tab, if it has one. */
  action?: ReactNode;
  onSelect: (tab: WorkTabId) => void;
}) {
  const strip = useRef<HTMLDivElement>(null);

  /**
   * Arrow keys select, and take the focus with them.
   *
   * Selection follows focus, which is the right pattern here because every tab
   * is already loaded — switching costs a re-render of a list the client holds,
   * not a fetch, so there is nothing to protect the reader from arrowing
   * through. Wraps at both ends; Home and End go to the edges.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = KEY_STEPS[event.key];
    if (step === undefined) return;

    const current = tabs.findIndex((tab) => tab.active);
    if (current === -1) return;

    const next =
      step === "home"
        ? 0
        : step === "end"
          ? tabs.length - 1
          : (current + step + tabs.length) % tabs.length;

    const moved = tabs[next];
    if (!moved) return;

    event.preventDefault();
    onSelect(moved.id);
    // The button is only re-rendered with tabIndex 0 after the selection lands,
    // so the focus is moved on the element the DOM already has.
    strip.current?.querySelector<HTMLButtonElement>(`#work-tab-${moved.id}`)?.focus();
  };

  return (
    <div className="work__tabbar">
      <div className="work__tabs" role="tablist" ref={strip} onKeyDown={onKeyDown}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`work-tab-${tab.id}`}
            aria-selected={tab.active}
            aria-controls={panelId}
            // One stop for the whole strip: Tab reaches the tabs, the arrows
            // move within them. One stop per tab is the anti-pattern the role
            // exists to collapse.
            tabIndex={tab.active ? 0 : -1}
            // Read by .work__tab::after, which reserves the label's selected
            // width so choosing a tab cannot reflow the row.
            data-label={tab.label}
            className={["work__tab", tab.active ? "work__tab--on" : ""]
              .filter((name) => name !== "")
              .join(" ")}
            onClick={() => onSelect(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {action}
    </div>
  );
}

/**
 * A part of the record the registry cannot answer yet.
 *
 * Wears its tag rather than styling alone, so nothing here can be mistaken for
 * a finding — an empty Condition panel and a work with no condition report are
 * different statements, and this is the first one.
 */
export function NotYetPanel({
  lead,
  detail,
  title,
}: {
  lead: string;
  detail: string;
  /** The heading, where the panel is one of several stacked in a column. */
  title?: string;
}) {
  return (
    <section className="work__notyet">
      <p className="work__notyet-tag">{NOT_YET_TAG}</p>
      {title !== undefined && <h3 className="work__notyet-title">{title}</h3>}
      <p className="work__notyet-lead">{lead}</p>
      <p className="work__notyet-detail">{detail}</p>
    </section>
  );
}
